import type { QueryClient } from '@tanstack/react-query'

import type { DashboardUser } from '../auth/auth-adapter'
import type { OnboardingApiClient } from '../onboarding/onboarding-api'
import type { OrganizationResponse } from '../onboarding/organization-onboarding-contracts'

/**
 * Settings > Organization reads. Each is a full server-side authorization of
 * the organization, so a result is reused briefly instead of asked again on
 * every tab switch; any change made here refreshes it at once.
 */
export const ORGANIZATION_ACCESS_STALE_MS = 30_000
/** The live, read-only Twilio check. "Recheck status" asks again on demand. */
export const PROVIDER_CHECK_STALE_MS = 120_000

export function managerAccessQuery(
  api: OnboardingApiClient,
  user: DashboardUser | null,
  organizationId: string
) {
  return {
    queryKey: ['organization-access-management', organizationId],
    queryFn: () => api.getOrganizationAccess!(user!),
    retry: false,
    staleTime: ORGANIZATION_ACCESS_STALE_MS,
  } as const
}

export function administratorAccessQuery(
  api: OnboardingApiClient,
  user: DashboardUser | null,
  organizationId: string
) {
  return {
    queryKey: ['organization-administrator-access', organizationId],
    queryFn: () => api.getAdministratorAccess!(user!),
    retry: false,
    staleTime: ORGANIZATION_ACCESS_STALE_MS,
  } as const
}

/**
 * Starts the Organization reads as soon as an Owner or Admin shows intent to
 * open the tab, so its people are usually ready when it opens.
 */
export function prefetchOrganizationAccess(
  queryClient: QueryClient,
  api: OnboardingApiClient,
  user: DashboardUser,
  organization: OrganizationResponse
) {
  const id = organization.organization.id
  if (api.getOrganizationAccess)
    void queryClient.prefetchQuery(managerAccessQuery(api, user, id))
  if (api.getAdministratorAccess)
    void queryClient.prefetchQuery(administratorAccessQuery(api, user, id))
}
